import { useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useConfirm } from '@/hooks/useConfirm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useOriginGroups } from '@/hooks/data/useOriginGroups';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { PlusIcon } from '@/components/icons';
import {
  ClearFiltersButton,
  ErrorAlert,
  PageHeader,
  SearchField,
} from '@/components/PageParts';
import { OriginGroupModal } from '@/components/OriginGroupModal';
import {
  OriginGroupsTable,
  type OriginGroupRowAction,
} from '@/components/originGroups/OriginGroupsTable';
import {
  ConnectionTestModal,
  type ConnectionTestOutcome,
} from '@/components/originGroups/ConnectionTestModal';
import { originHealthMap } from '@/config/statusMaps';
import { getOriginGroupActions } from '@/config/resourceRules';
import { pluralize } from '@/utils/format';
import type { OriginGroup, OriginHealthStatus } from '@/types/originGroup';
import './OriginGroups.css';

const SEARCH_DEBOUNCE_MS = 300;

const TLS_FILTER_OPTIONS = [
  { value: 'true', label: 'HTTPS' },
  { value: 'false', label: 'HTTP' },
] as const;

/**
 * Origin Groups page component
 *
 * Features:
 * - List origin groups with health and TLS filters (search debounced)
 * - Create, edit, delete and test origin groups (operator+)
 */
export function OriginGroups() {
  const { role, can } = usePermissions();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [healthFilter, setHealthFilter] = useState<OriginHealthStatus | ''>('');
  const [tlsFilter, setTlsFilter] = useState<'true' | 'false' | ''>('');
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), SEARCH_DEBOUNCE_MS);

  const {
    originGroups,
    total,
    isLoading,
    error: loadError,
    reload,
    createOriginGroup,
    updateOriginGroup,
    deleteOriginGroup,
    testConnection,
  } = useOriginGroups({
    health_status: healthFilter || undefined,
    enable_tls: tlsFilter ? tlsFilter === 'true' : undefined,
    search: debouncedSearch || undefined,
  });

  // UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOriginGroup, setSelectedOriginGroup] = useState<OriginGroup | null>(null);
  const [testOutcome, setTestOutcome] = useState<ConnectionTestOutcome | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const testAction = useAsyncAction((og: OriginGroup) => testConnection(og.id), {
    errorMessage: 'Connection test failed',
    getKey: (og) => og.id,
  });

  const openModal = (og: OriginGroup | null) => {
    setSelectedOriginGroup(og);
    setIsModalOpen(true);
  };

  /**
   * Test the connection; failures are shown in the same result dialog
   */
  const handleTestConnection = async (og: OriginGroup) => {
    const outcome = await testAction.run(og);
    setTestOutcome(
      outcome.ok
        ? {
            originName: og.name,
            success: outcome.value.success,
            responseTimeMs: outcome.value.response_time_ms,
            error: outcome.value.error || undefined,
          }
        : { originName: og.name, success: false, error: outcome.error }
    );
  };

  const handleRowAction = (action: OriginGroupRowAction, og: OriginGroup) => {
    setActionError(null);
    switch (action) {
      case 'test':
        void handleTestConnection(og);
        return;
      case 'edit':
        openModal(og);
        return;
      case 'delete':
        void confirm({
          title: 'Delete Origin Group',
          message: (
            <>
              Are you sure you want to delete <strong>{og.name}</strong>? This action cannot be undone.
            </>
          ),
          confirmLabel: 'Delete',
          onConfirm: () => deleteOriginGroup(og.id),
          errorMessage: 'Failed to delete origin group',
        });
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setHealthFilter('');
    setTlsFilter('');
  };

  const handleRetry = () => {
    setActionError(null);
    void reload();
  };

  const hasActiveFilters = Boolean(searchQuery || healthFilter || tlsFilter);
  const canCreate = can('origin-groups:create');

  return (
    <div className="origin-groups-page">
      <PageHeader
        classPrefix="origin-groups"
        title="Origin Groups"
        count={pluralize(total, 'origin')}
        actions={
          canCreate && (
            <Button leftIcon={<PlusIcon />} onClick={() => openModal(null)}>
              Add Origin Group
            </Button>
          )
        }
      />

      {/* Filters */}
      <div className="origin-groups-filters">
        <SearchField
          classPrefix="origin-groups"
          placeholder="Search origin groups..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        <Select
          value={healthFilter}
          onChange={setHealthFilter}
          placeholder="All Health"
          options={originHealthMap.options}
          className="origin-groups-filter-select"
          aria-label="Filter by health"
        />

        <Select
          value={tlsFilter}
          onChange={setTlsFilter}
          placeholder="All Protocol"
          options={TLS_FILTER_OPTIONS}
          className="origin-groups-filter-select"
          aria-label="Filter by protocol"
        />

        {hasActiveFilters && <ClearFiltersButton classPrefix="origin-groups" onClick={handleClearFilters} />}
      </div>

      <ErrorAlert classPrefix="origin-groups" message={actionError ?? loadError} onRetry={handleRetry} />

      <OriginGroupsTable
        originGroups={originGroups}
        isLoading={isLoading}
        onAddFirst={canCreate ? () => openModal(null) : undefined}
        getActions={(og) => getOriginGroupActions(role, og)}
        isBusy={(_action, id) => testAction.isPending(id)}
        onAction={handleRowAction}
      />

      <OriginGroupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={createOriginGroup}
        onUpdate={updateOriginGroup}
        originGroup={selectedOriginGroup}
      />

      <ConnectionTestModal outcome={testOutcome} onClose={() => setTestOutcome(null)} />

      {confirmDialog}
    </div>
  );
}
